import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './PresentationApi';
import { Presentation, PresentationRequest } from './PresentationTypes';

const presentationKey = getQueryKey('presentation');

export function getPresentationPagedOptions(params: PagedParams): UseSuspenseQueryOptions<PagedResponse<Presentation>> {
  return {
    queryKey: presentationKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getPresentationOptions(presentationId: Id): UseSuspenseQueryOptions<Presentation> {
  return {
    queryKey: presentationKey.one(presentationId),
    queryFn: () => api.get(presentationId),
  };
}

export function usePresentationEdit(presentationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: PresentationRequest) => api.edit(presentationId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: presentationKey.root(),
      }),
  });
}

export function usePresentationCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: PresentationRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: presentationKey.root(),
      }),
  });
}

export function usePresentationDelete(presentationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(presentationId),
    onSuccess: () => invalidateAfterDelete(queryClient, presentationKey, presentationId),
  });
}
